import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getTranslation, formatTranslation } from './translations';

// 支持的语言类型
export type Language = 'en' | 'zh-CN' | 'zh-TW';

// 翻译资源接口
export interface TranslationResources {
  [key: string]: string;
}

// 语言包接口
export interface LanguagePack {
  [key: string]: TranslationResources;
}

// 语言配置接口
export interface LanguageConfig {
  code: Language;
  name: string;
  nativeName: string;
}

// 支持的语言列表
export const SUPPORTED_LANGUAGES: LanguageConfig[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '简体中文' }
];

// Context接口
export interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  supportedLanguages: LanguageConfig[];
  getCurrentLanguageConfig: () => LanguageConfig;
  t: (key: string, params?: Record<string, string | number>) => string;
}

// 创建Context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Provider组件属性
export interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

// Provider组件
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children, 
  defaultLanguage 
}) => {
  // 检测浏览器语言
  const detectBrowserLanguage = (): Language => {
    const browserLang = navigator.language || (navigator as any).userLanguage;
    
    // 检查是否支持检测到的语言
    const detectedLang = SUPPORTED_LANGUAGES.find(lang => 
      browserLang.startsWith(lang.code)
    );
    
    if (detectedLang) {
      return detectedLang.code;
    }
    
    // 如果浏览器语言是中文变体，默认使用简体中文
    if (browserLang.startsWith('zh')) {
      return browserLang.includes('TW') || browserLang.includes('HK') ? 'zh-TW' : 'zh-CN';
    }
    
    // 默认返回英语
    return 'en';
  };

  // 从localStorage获取保存的语言设置，如果没有则使用默认语言或检测浏览器语言
  const getInitialLanguage = (): Language => {
    // 优先使用传入的默认语言
    if (defaultLanguage) {
      return defaultLanguage;
    }
    
    // 尝试从localStorage获取
    const savedLanguage = localStorage.getItem('preferred-language') as Language;
    if (savedLanguage && SUPPORTED_LANGUAGES.some(lang => lang.code === savedLanguage)) {
      return savedLanguage;
    }
    
    // 检测浏览器语言
    return detectBrowserLanguage();
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  // 设置语言并保存到localStorage
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem('preferred-language', newLanguage);
    // 更新HTML lang属性
    document.documentElement.lang = newLanguage;
  };

  // 获取当前语言配置
  const getCurrentLanguageConfig = (): LanguageConfig => {
    return SUPPORTED_LANGUAGES.find(lang => lang.code === language) || SUPPORTED_LANGUAGES[0];
  };

  // 翻译函数
  const t = (key: string, params?: Record<string, string | number>): string => {
    if (params) {
      return formatTranslation(key, language, params);
    }
    return getTranslation(key, language);
  };

  // 初始化时设置HTML lang属性
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    getCurrentLanguageConfig,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// 自定义Hook
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;